import React, { useEffect, useState, useRef } from "react";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { useJwt } from "react-jwt";
import { EditStoreAPI, GetstoreById, api_url } from "../apiconfig/authService";
import { RiLoaderFill } from "react-icons/ri";
import { toast } from "react-toastify";

function EditStore(props) {
  const navigate = useNavigate();
  const { _, isExpired } = useJwt(localStorage.getItem("accessToken"));
  if (isExpired) navigate("/");

  const [mediaData, setMediaData] = useState({});
  const [editedMediaData, setEditedMediaData] = useState({});
  const [newImage, setNewImage] = useState(null);
  const [newImageSupport, setNewImageSupport] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [newImageSupportPreview, setNewImageSupportPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const { storeid } = useParams();

  // ✅ separate refs for each file input
  const storeImageInputRef = useRef();
  const supportImageInputRef = useRef();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const response = await GetstoreById(
          storeid,
          localStorage.getItem("accessToken")
        );
        setLoading(false);
        setMediaData(response.data._doc);
        setEditedMediaData(response.data._doc);
      } catch (error) {
        console.error("Error fetching store data:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [storeid]);

  const handleChange = (field) => (e) => {
    const temp = { ...editedMediaData };
    temp[field] = e.target.value;
    setEditedMediaData(temp);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setNewImage(file);
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setNewImagePreview(previewUrl);
    }
  };

  const handleImageSupportChange = (e) => {
    const file = e.target.files[0];
    setNewImageSupport(file);
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setNewImageSupportPreview(previewUrl);
    }
  };

  const handleDiscardClick = () => {
    if (storeImageInputRef.current) storeImageInputRef.current.value = "";
    if (supportImageInputRef.current) supportImageInputRef.current.value = "";

    setNewImage(null);
    setNewImagePreview(null);

    setNewImageSupport(null);
    setNewImageSupportPreview(null);

    setEditedMediaData({ ...mediaData });
    navigate("/stores");
  };

  const handleSubmitClick = async (e) => {
    e.preventDefault();
    try {
      if (editedMediaData.storename === "") {
        toast.error("Store Name is required!");
        return;
      }
      const urlPattern = /^https:\/\/www\.[a-zA-Z0-9-]+\.[a-z]{2,}$/;
      if (editedMediaData.weburl === "") {
        toast.error("Web URL is required!");
        return;
      }
      if (!urlPattern.test(editedMediaData.weburl)) {
        toast.error(
          "Please enter a valid Web URL (e.g., https://www.example.com)"
        );
        return;
      }
      if (editedMediaData.supporturl === "") {
        toast.error("Support URL is required!");
        return;
      }
      if (!urlPattern.test(editedMediaData.supporturl)) {
        toast.error(
          "Please enter a valid Support URL (e.g., https://www.example.com)"
        );
        return;
      }
    } catch (error) {
      toast.error(error.message);
      window.scrollTo(0, 0);
      return;
    }

    const formData = new FormData();
    formData.append("storeid", editedMediaData.storeid);
    formData.append("storename", editedMediaData.storename);
    formData.append("weburl", editedMediaData.weburl);
    formData.append("supporturl", editedMediaData.supporturl);

    if (newImage) {
      formData.append("image", newImage);
      formData.append("imagename", editedMediaData.image);
    }

    if (newImageSupport) {
      formData.append("supportimage", newImageSupport);
      formData.append("supportimagename", editedMediaData.supportimage);
    }

    try {
      setEditLoading(true);
      const response = await EditStoreAPI(
        formData,
        localStorage.getItem("accessToken")
      );
      if (response.status === 200) {
        setEditLoading(false);
        toast.success("Store Updated Successfully");
        navigate("/stores");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        "An error occurred. Please try again later.";
      toast.error(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="dashboard-content">
      <Sidebar />
      <div className="body-cart tab-content dasContent p-4 bg-white rounded">
        <div className="d-flex justify-content-between align-items-start pb-4 border-bottom mb-4">
          <h1 className="heading h4 mb-0 fw-bold">Edit Store</h1>
          <NavLink to="/stores" className="btn btn-primary">
            View All
          </NavLink>
        </div>

        {loading ? (
          <div className="d-flex align-items-center justify-content-center">
            <RiLoaderFill
              size={40}
              className="text-success spin-animation text-center"
            />
          </div>
        ) : (
          <div className="tab-pane active storetab">
            <div className="row">
              <div className="form-group col-sm-12 col-lg-6 mb-3">
                <label>Store Name</label>
                <input
                  type="text"
                  value={editedMediaData?.storename || ""}
                  onChange={handleChange("storename")}
                  className="form-control custom-input my-3"
                  placeholder="Store Name"
                />
              </div>
              <div className="form-group col-sm-12 col-lg-6 mb-3">
                <label>Web URL</label>
                <input
                  type="text"
                  value={editedMediaData?.weburl || ""}
                  onChange={handleChange("weburl")}
                  className="form-control custom-input my-3"
                  placeholder="Web URL"
                />
              </div>
            </div>
            <div className="row">
              <div className="form-group col-sm-12 col-lg-6 mb-3">
                <label>Store Image</label>
                <input
                  type="file"
                  ref={storeImageInputRef}
                  className="form-control custom-input my-3"
                  onChange={handleImageChange}
                />
                {newImagePreview ? (
                  <img
                    width="100px"
                    height="100px"
                    className="my-2"
                    src={newImagePreview}
                    alt="New Preview"
                  />
                ) : editedMediaData?.image ? (
                  <img
                    width="100px"
                    height="100px"
                    className="my-2"
                    src={`${api_url}/product/category/getCategoryImage/${editedMediaData.image}`}
                    alt="Current"
                  />
                ) : null}
              </div>

              <div className="form-group col-sm-12 col-lg-6 mb-3">
                <label>Support URL</label>
                <input
                  type="text"
                  value={editedMediaData?.supporturl || ""}
                  onChange={handleChange("supporturl")}
                  className="form-control custom-input my-3"
                  placeholder="Support URL"
                />
              </div>
            </div>
            <div className="row">
              <div className="form-group col-sm-12 col-lg-6 mb-3">
                <label>Support Image</label>
                <input
                  type="file"
                  ref={supportImageInputRef}
                  className="form-control custom-input my-3"
                  onChange={handleImageSupportChange}
                />
                {newImageSupportPreview ? (
                  <img
                    width="100px"
                    height="100px"
                    className="my-2"
                    src={newImageSupportPreview}
                    alt="New Support Preview"
                  />
                ) : editedMediaData?.supportimage ? (
                  <img
                    width="100px"
                    height="100px"
                    className="my-2"
                    src={`${api_url}/product/category/getCategoryImage/${editedMediaData.supportimage}`}
                    alt="Current Support"
                  />
                ) : null}
              </div>
            </div>
            <div className="row">
              <div
                className="col-12 d-flex"
                style={{ height: "40px", width: "120px" }}
              >
                <button
                  type="submit"
                  onClick={handleDiscardClick}
                  disabled={editLoading}
                  style={{ height: "100%", width: "100%" }}
                  className="btn btn-danger save-btn text-uppercase"
                >
                  Discard
                </button>{" "}
                &nbsp;
                {editLoading ? (
                  <div
                    className="btn btn-primary loader-active save-btn"
                    style={{ height: "100%", width: "100%" }}
                  >
                    <RiLoaderFill
                      size={20}
                      className="spin-animation"
                      color="white"
                    />
                  </div>
                ) : (
                  <button
                    type="submit"
                    disabled={editLoading}
                    onClick={handleSubmitClick}
                    className="btn btn-primary save-btn text-uppercase"
                    style={{ height: "100%", width: "100%" }}
                  >
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EditStore;
